import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { INITIAL_FORM_DATA, STEP_IDS } from '../types/lectureForm.types';

const FormContext = createContext();

// Action types
const FORM_ACTIONS = {
  UPDATE_FIELD: 'UPDATE_FIELD',
  UPDATE_STEP_DATA: 'UPDATE_STEP_DATA',
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  MARK_STEP_COMPLETED: 'MARK_STEP_COMPLETED',
  MARK_STEP_UNCOMPLETED: 'MARK_STEP_UNCOMPLETED',
  RESET_FORM: 'RESET_FORM',
  SET_VALIDATION_ERROR: 'SET_VALIDATION_ERROR',
  CLEAR_VALIDATION_ERROR: 'CLEAR_VALIDATION_ERROR',
  LOAD_LECTURE_DATA: 'LOAD_LECTURE_DATA'
};

// Form reducer
function formReducer(state, action) {
  switch (action.type) {
    case FORM_ACTIONS.UPDATE_FIELD:
      return {
        ...state,
        [action.field]: action.value
      };

    case FORM_ACTIONS.UPDATE_STEP_DATA:
      return {
        ...state,
        ...action.data
      };

    case FORM_ACTIONS.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.step
      };

    case FORM_ACTIONS.MARK_STEP_COMPLETED:
      const completedSteps = [...state.completedSteps];
      if (!completedSteps.includes(action.step)) {
        completedSteps.push(action.step);
      }
      return {
        ...state,
        completedSteps
      };

    case FORM_ACTIONS.MARK_STEP_UNCOMPLETED:
      const filteredSteps = state.completedSteps.filter(step => step !== action.step);
      return {
        ...state,
        completedSteps: filteredSteps
      };

    case FORM_ACTIONS.SET_VALIDATION_ERROR:
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.field]: action.error
        }
      };

    case FORM_ACTIONS.CLEAR_VALIDATION_ERROR:
      const { [action.field]: removed, ...restErrors } = state.validationErrors || {};
      return {
        ...state,
        validationErrors: restErrors
      };

    case FORM_ACTIONS.RESET_FORM:
      return {
        ...INITIAL_FORM_DATA,
        validationErrors: {}
      };

    case FORM_ACTIONS.LOAD_LECTURE_DATA:
      // Preserve currentStep and completedSteps if we're already in edit mode
      // Only override them if they're explicitly provided in the loaded data
      return {
        ...state,
        ...action.data,
        currentStep: action.data.currentStep || state.currentStep,
        completedSteps: action.data.completedSteps || state.completedSteps || [],
        validationErrors: state.validationErrors || {}
      };

    default:
      return state;
  }
}

export function FormProvider({ children }) {
  const [formData, dispatch] = useReducer(formReducer, {
    ...INITIAL_FORM_DATA,
    validationErrors: {}
  });
  
  // Track initial state to detect changes
  const [initialFormData, setInitialFormData] = React.useState(null);

  // Actions
  const updateField = useCallback((field, value) => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_FIELD,
      field,
      value
    });
  }, []);

  const updateStepData = useCallback((data) => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_STEP_DATA,
      data
    });
  }, []);

  const setCurrentStep = useCallback((step) => {
    dispatch({
      type: FORM_ACTIONS.SET_CURRENT_STEP,
      step
    });
  }, []);

  const markStepCompleted = useCallback((step) => {
    dispatch({
      type: FORM_ACTIONS.MARK_STEP_COMPLETED,
      step
    });
  }, []);

  const markStepUncompleted = useCallback((step) => {
    dispatch({
      type: FORM_ACTIONS.MARK_STEP_UNCOMPLETED,
      step
    });
  }, []);

  const setValidationError = useCallback((field, error) => {
    dispatch({
      type: FORM_ACTIONS.SET_VALIDATION_ERROR,
      field,
      error
    });
  }, []);

  const clearValidationError = useCallback((field) => {
    dispatch({
      type: FORM_ACTIONS.CLEAR_VALIDATION_ERROR,
      field
    });
  }, []);

  const resetForm = useCallback(() => {
    dispatch({
      type: FORM_ACTIONS.RESET_FORM
    });
  }, []);

  const loadLectureData = useCallback((lectureData) => {
    dispatch({
      type: FORM_ACTIONS.LOAD_LECTURE_DATA,
      data: lectureData
    });
    // Set initial form data when loading a lecture
    setInitialFormData(lectureData);
  }, []);

  // Helper functions
  const isStepCompleted = useCallback((step) => {
    return formData.completedSteps.includes(step);
  }, [formData.completedSteps]);

  const getFieldError = useCallback((field) => {
    return formData.validationErrors?.[field];
  }, [formData.validationErrors]);

  // Helper function to handle automatic completion
  const handleFieldChange = useCallback((field, value) => {
    updateField(field, value);
  }, [updateField]);
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // If no initial data was set (new lecture), check if any data was entered
    if (!initialFormData) {
      // Check if any meaningful data was entered (not just empty initial state)
      // Check various fields to see if user has entered anything
      const hasTitle = formData.title && formData.title.length > 0 && formData.title !== '';
      const hasDescription = formData.description && formData.description.length > 0 && formData.description !== '';
      const hasSpeakers = formData.speakers && formData.speakers.length > 0;
      const hasTopics = formData.topics && formData.topics.length > 0;
      const hasSubjects = formData.subjects && formData.subjects.length > 0;
      const hasHandout = formData.handoutFiles && formData.handoutFiles.length > 0;
      const hasPresentation = formData.presentationFiles && formData.presentationFiles.length > 0;
      const hasImage = formData.imageFile && formData.imageFile.length > 0;
      const hasLanguage = formData.presentationLanguage && formData.presentationLanguage !== '';
      const hasType = formData.type && formData.type !== '';
      
      return hasTitle || hasDescription || hasSpeakers || hasTopics || hasSubjects || 
             hasHandout || hasPresentation || hasImage || hasLanguage || hasType;
    }
    
    // If initial data exists (editing), compare with current state
    // Create comparable objects without validation errors and other runtime properties
    const currentComparable = {
      ...formData,
      validationErrors: undefined,
      currentStep: undefined,
      completedSteps: undefined
    };
    
    const initialComparable = {
      ...initialFormData,
      validationErrors: undefined,
      currentStep: undefined,
      completedSteps: undefined
    };
    
    // Function to normalize and sort object keys recursively for consistent comparison
    const normalizeAndSort = (obj) => {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }
      
      if (obj instanceof Array) {
        // Filter out empty objects and empty strings from arrays
        const filtered = obj.filter(item => {
          if (typeof item === 'string') {
            return item.trim() !== '';
          }
          if (typeof item === 'object' && item !== null) {
            // Check if object has only empty string values
            const values = Object.values(item);
            return values.some(val => val !== '' && val !== null && val !== undefined);
          }
          return item !== null && item !== undefined;
        });
        
        return filtered.map(item => normalizeAndSort(item));
      }
      
      const sorted = {};
      Object.keys(obj).sort().forEach(key => {
        sorted[key] = normalizeAndSort(obj[key]);
      });
      return sorted;
    };
    
    const currentSorted = normalizeAndSort(currentComparable);
    const initialSorted = normalizeAndSort(initialComparable);
    
    const currentJson = JSON.stringify(currentSorted);
    const initialJson = JSON.stringify(initialSorted);
    
    const hasChanges = currentJson !== initialJson;
    
    console.log('=== hasUnsavedChanges Check (Edit Mode) ===');
    console.log('Initial data keys:', Object.keys(initialFormData || {}));
    console.log('Current data keys:', Object.keys(formData));
    console.log('Initial sorted length:', initialJson.length);
    console.log('Current sorted length:', currentJson.length);
    console.log('Has changes:', hasChanges);
    if (hasChanges) {
      console.log('Initial sorted :', initialJson);
      console.log('Current sorted :', currentJson);
    }
    
    return hasChanges;
  }, [formData, initialFormData]);

  const contextValue = {
    // State
    formData,
    
    // Actions
    updateField,
    updateStepData,
    setCurrentStep,
    markStepCompleted,
    markStepUncompleted,
    setValidationError,
    clearValidationError,
    resetForm,
    loadLectureData,
    
    // Helpers
    isStepCompleted,
    getFieldError,
    handleFieldChange,
    hasUnsavedChanges
  };

  return (
    <FormContext.Provider value={contextValue}>
      {children}
    </FormContext.Provider>
  );
}

export function useFormContext() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}